import { Injectable } from '@angular/core';
import { CompanyModel } from 'app/models/company.model';
import { CompanyService } from 'app/services/company.service';
import { BehaviorSubject, Observable } from 'rxjs';

import { map } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class TimezoneService {
    private _timezone$ = new BehaviorSubject<string>(this.getBrowserTimezone());
    /** When true, currentCompany$ must not overwrite the active timezone. */
    private _overrideActive = false;
    private _lastCompanyTimezone: string | null = null;
    /** /admin/home com "Todos": libera o dropdown de fuso no header. */
    private _allowClockCompanyPicker$ = new BehaviorSubject<boolean>(false);

    timezone$: Observable<string> = this._timezone$.asObservable();
    friendlyTimezone$: Observable<string> = this._timezone$.pipe(
        map((tz) => this.getFriendlyTimezoneLabel(tz))
    );
    allowClockCompanyPicker$: Observable<boolean> = this._allowClockCompanyPicker$.asObservable();

    constructor(private companyService: CompanyService) {
        this.companyService.currentCompany$.subscribe((company) => {
            this._lastCompanyTimezone = company?.timezone ?? this.getBrowserTimezone();
            if (this._overrideActive) {
                return;
            }
            this._timezone$.next(this._lastCompanyTimezone);
        });
    }

    getTimezone(): string {
        return this._timezone$.value;
    }

    hasTimezoneOverride(): boolean {
        return this._overrideActive;
    }

    isClockCompanyPickerAllowed(): boolean {
        return this._allowClockCompanyPicker$.value;
    }

    /** Liberado por totals-kpi (/admin/home) quando o filtro é "Todos". */
    setAllowClockCompanyPicker(allow: boolean): void {
        this._allowClockCompanyPicker$.next(!!allow);
    }

    /**
     * Fix timezone for Admin/Motor context. While set, currentCompany$ is ignored.
     * Callers: classic.component (clock menu), allocation-engine (single-company filter).
     */
    setTimezoneOverride(tz: string | null): void {
        if (!tz) {
            this.clearTimezoneOverride();
            return;
        }
        this._overrideActive = true;
        this._timezone$.next(tz);
    }

    setTimezoneCompanyOverride(company: CompanyModel | null): void {
        if (!company?.timezone) {
            this.clearTimezoneOverride();
            return;
        }
        this.setTimezoneOverride(company.timezone);
    }

    /** Restore timezone from currentCompany (or browser). */
    clearTimezoneOverride(): void {
        this._overrideActive = false;
        this._timezone$.next(this._lastCompanyTimezone ?? this.getBrowserTimezone());
    }

    getFriendlyTimezoneLabel(tz: string): string {
        if (!tz) return '';
        
        // Mapeamento amigável para fusos horários comuns
        const timezoneLabels: Record<string, string> = {
            'America/Sao_Paulo': 'Brasília (UTC-3)',
            'America/Fortaleza': 'Fortaleza (UTC-3)',
            'America/Manaus': 'Manaus (UTC-4)',
            'America/Rio_Branco': 'Acre (UTC-5)',
            'America/Noronha': 'F. de Noronha (UTC-2)',
            'Europe/Lisbon': 'Lisboa (UTC+0)',
            'Atlantic/Azores': 'Açores (UTC-1)',
            'Europe/Madrid': 'Madri (UTC+1)',
            'Europe/Paris': 'Paris (UTC+1)',
            'Europe/Berlin': 'Berlim (UTC+1)',
            'America/New_York': 'Nova Iorque (UTC-5)',
            'America/Chicago': 'Chicago (UTC-6)',
            'America/Denver': 'Denver (UTC-7)',
            'America/Los_Angeles': 'Los Angeles (UTC-8)',
            'Asia/Shanghai': 'Xangai (UTC+8)',
            'Asia/Tokyo': 'Tóquio (UTC+9)',
            'Europe/Moscow': 'Moscou (UTC+3)',
            'UTC': 'UTC'
        };

        if (timezoneLabels[tz]) {
            return timezoneLabels[tz];
        }

        // Formatação dinâmica amigável para os demais
        return tz.split('/').pop()?.replace(/_/g, ' ') || tz;
    }

    private getBrowserTimezone(): string {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
}
